/**
 * Cookie-based authentication session manager
 *
 * Tokens are stored in httpOnly cookies (set by the server) and are invisible
 * to client JavaScript. This module provides helpers for:
 *   - Reading the non-httpOnly __csrf cookie (needed for CSRF header)
 *   - Triggering a token refresh via the server (cookies sent automatically)
 *   - Clearing legacy localStorage tokens from the pre-cookie migration
 *
 * The access token cookie (__access_token) is httpOnly — the client cannot
 * read it directly. Authentication state is determined by calling /auth/me.
 */

import { API_CONFIG } from '../config/api.config';
import { logger } from './logger';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

const CSRF_COOKIE_NAME = '__csrf';
const TENANT_COOKIE_NAME = '__tenant_id';

export class TokenManager {
  private static refreshPromise: Promise<boolean> | null = null;
  private static csrfToken: string | null = null;

  /**
   * In-memory CSRF token captured from auth responses (signin, refresh,
   * /auth/me). Required when the __csrf cookie is host-only on the API
   * domain and unreadable from a different client origin.
   */
  static setCsrfToken(token: string | null | undefined): void {
    this.csrfToken = typeof token === 'string' && token.length > 0 ? token : null;
  }

  /**
   * CSRF token for the X-CSRF-Token header. Prefers the in-memory value
   * captured from auth responses; falls back to the __csrf cookie for
   * envs where the client and API share a parent domain.
   */
  static getCsrfToken(): string | null {
    if (this.csrfToken) return this.csrfToken;
    try {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  /**
   * No-op for backward compatibility.
   * Tokens are set as httpOnly cookies by the server.
   * Clears any legacy localStorage tokens.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static setTokens(_tokens: AuthTokens): void {
    this.clearLegacyTokens();
  }

  /**
   * Not available with httpOnly cookies.
   * Kept for backward compatibility — returns null.
   */
  static getAccessToken(): string | null {
    return null;
  }

  /**
   * Not available with httpOnly cookies.
   * Kept for backward compatibility — returns null.
   */
  static getRefreshToken(): string | null {
    return null;
  }

  /**
   * Clear legacy localStorage tokens from before the cookie migration,
   * and the in-memory CSRF token so a stale value isn't sent post-logout.
   */
  static clearTokens(): void {
    this.clearLegacyTokens();
    this.csrfToken = null;
  }

  /**
   * Refresh access token by calling the server.
   * The httpOnly refresh token cookie is sent automatically by the browser.
   * Returns true if refresh succeeded, false otherwise.
   */
  static async refreshAccessToken(): Promise<boolean> {
    // Deduplicate: if a refresh is already in progress, reuse its promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const csrfToken = this.getCsrfToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (csrfToken) {
          headers['X-CSRF-Token'] = csrfToken;
        }

        const response = await fetch(`${API_CONFIG.apiUrl}/auth/token/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          logger.error('[TokenManager] Refresh failed:', { error: errorData });
          return false;
        }

        const body = await response.json().catch(() => ({}));
        if (body && typeof body.csrfToken === 'string') {
          this.setCsrfToken(body.csrfToken);
        }
        return true;
      } catch (error) {
        logger.error('[TokenManager] Token refresh failed:', { error });
        return false;
      } finally {
        // Clear the promise so future refresh calls can proceed
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Token validity cannot be determined client-side with httpOnly cookies.
   * Returns false — callers should rely on API responses.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static isTokenValid(_token: string): boolean {
    return false;
  }

  /**
   * Token expiry cannot be determined client-side with httpOnly cookies.
   * Returns true — callers should rely on API responses.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static isTokenExpired(_token: string): boolean {
    return true;
  }

  /**
   * Cannot determine refresh need client-side with httpOnly cookies.
   * Returns false — server signals via X-Token-Refresh-Needed header.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static needsRefresh(_token: string): boolean {
    return false;
  }

  /**
   * Store the selected tenant ID in a non-httpOnly cookie.
   */
  static setTenantId(tenantId: string): void {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${TENANT_COOKIE_NAME}=${encodeURIComponent(tenantId)}; path=/; SameSite=Lax; max-age=31536000${secure}`;
  }

  /**
   * Read the selected tenant ID from the cookie.
   */
  static getTenantId(): string | null {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${TENANT_COOKIE_NAME}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  /**
   * Clear the tenant ID cookie.
   */
  static clearTenantId(): void {
    document.cookie = `${TENANT_COOKIE_NAME}=; path=/; SameSite=Lax; max-age=0`;
  }

  /**
   * Remove legacy tokens from localStorage (pre-cookie migration cleanup)
   */
  private static clearLegacyTokens(): void {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    } catch {
      // Ignore errors (e.g. SSR, privacy mode)
    }
  }
}

// Export singleton instance for easy use
export const tokenManager = TokenManager;
